/**
 * Drawer Position and Size Utilities
 * 
 * Pure functions for mapping drawer configuration to Mantine position and size values.
 */

import type { DrawerConfig } from "../types.js";

/**
 * Map drawer side to Mantine position
 * 
 * @param side - Drawer side ("top" | "bottom" | "left" | "right")
 * @returns Mantine position value
 */
export function mapSideToPosition(side: DrawerConfig["side"]): "top" | "bottom" | "left" | "right" {
  switch (side) {
    case "left":
      return "left";
    case "right":
      return "right";
    case "top":
      return "top";
    case "bottom":
    default:
      return "bottom";
  }
}

/**
 * Map drawer height to Mantine size
 * 
 * Handles percentage, viewport height, and pixel values.
 * Defaults based on position if not specified.
 * 
 * @param height - Height value (e.g., "95vh", "80%", "500px")
 * @param side - Drawer side for default sizing
 * @returns Mantine size value
 */
export function mapHeightToSize(
  height: DrawerConfig["height"],
  side: DrawerConfig["side"]
): string {
  if (height) {
    // If height is a percentage or vh, use it directly
    if (typeof height === "string" && (height.includes("%") || height.includes("vh"))) {
      return height;
    }
    return height;
  }

  // Default sizes based on position. Bottom/top sheets default to fitting their
  // content (with a viewport cap applied by getViewportConstrainedStyles) rather
  // than a fixed 95vh — a fixed height leaves a large empty gap under short
  // menus/pickers. Callers can still pass an explicit height to opt out.
  switch (side) {
    case "bottom":
    case "top":
      return "auto";
    case "left":
    case "right":
      return "md";
    default:
      return "md";
  }
}

/**
 * Get viewport-constrained styles for bottom/top drawers
 *
 * @param side - Drawer side
 * @param autoHeight - true when the sheet sizes to its content (no explicit
 *   height). In that mode we cap the sheet at 85dvh so tall content scrolls
 *   instead of covering the whole screen. Explicit heights are left uncapped so
 *   callers that intentionally request 90vh/95vh keep their behavior.
 * @returns CSS-in-JS styles object
 */
export function getViewportConstrainedStyles(
  side: DrawerConfig["side"],
  autoHeight: boolean = false
) {
  if (side === "bottom" || side === "top") {
    return {
      width: "100vw",
      maxWidth: "100vw",
      left: "0",
      right: "0",
      margin: "0",
      overflowX: "hidden" as const,
      display: "flex" as const,
      flexDirection: "column" as const,
      ...(autoHeight ? { maxHeight: "85dvh" } : {}),
    };
  }
  return {};
}
